import { useEffect, useState } from 'react'
import { Modal, Box, Stack, Paper, Group, Text, Button, Title, Alert, Anchor } from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import { IconCheck, IconCreditCard } from '@tabler/icons-react'
import { GravatarQuickEditorCore } from '@gravatar-com/quick-editor'
import EditableField from '../ui/EditableField'
import logout from '../auth/logout'
import { useLazyQuery, useMutation, useQuery } from '@apollo/client/react'
import UsernameAvailable from '../graphql/queries/UsernameAvailable'
import UpdateUser from '../graphql/queries/UpdateUser'
import ResetPassword from '../graphql/queries/ResetPassword'
import RegisterGuest from '../graphql/queries/RegisterGuest'
import SendVerification from '../graphql/queries/SendVerification'
import UserBalance from '../graphql/queries/UserBalance'
import CreateCheckoutSession from '../graphql/queries/CreateCheckoutSession'
import ProfileAvatar from './ProfileAvatar'
import { useModalStack } from '../ui/ModalStack'
import CurrentUser from '../graphql/queries/CurrentUser'
import Joyride, { STATUS } from 'react-joyride'

function isValidEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
}

export default function ProfileModal({ opened, onClose, ...rest }) {

    const { data: { user } } = useQuery(CurrentUser)
    const { pushModal } = useModalStack()
    const isMobile = useMediaQuery('(max-width: 50em)')
    const originalUsername = user?.username
    const originalEmail = user?.email
    const [editing, setEditing] = useState('')
    const [username, setUsername] = useState(originalUsername ?? '')
    const [email, setEmail] = useState(originalEmail ?? '')
    const [emailError, setEmailError] = useState('')
    const [resetEmailSent, setResetEmailSent] = useState(false)
    const [registrationSuccess, setRegistrationSuccess] = useState(false)
    const [verificationSent, setVerificationSent] = useState(false)
    const [queryUsername, { data: queryUsernameData }] = useLazyQuery(UsernameAvailable, {
        fetchPolicy: 'cache-and-network',
        variables: { username }
    })
    const [updateUser, { error: updateUserError }] = useMutation(UpdateUser)
    const [resetPasswordMutation, { loading: resetPasswordLoading }] = useMutation(ResetPassword)
    const [sendVerificationMutation, { loading: sendVerificationLoading }] = useMutation(SendVerification)
    const [registerGuestMutation] = useMutation(RegisterGuest, {
        refetchQueries: [CurrentUser],
        awaitRefetchQueries: true
    })
    const [fetchBalance, { data: balanceData }] = useLazyQuery(UserBalance, {
        fetchPolicy: 'network-only'
    })
    const [createCheckoutSession, { loading: checkoutLoading }] = useMutation(CreateCheckoutSession)

    const isGuest = user?.roles?.includes('guest') ?? false
    const isVerified = user?.roles?.includes('verified') ?? false
    const needsVerification = !isGuest && !isVerified

    // Onboarding tour state
    const [runTour, setRunTour] = useState(false)
    const [hasSeenGuestOnboarding, setHasSeenGuestOnboarding] = useState(true)
    const [hasSeenRegisteredOnboarding, setHasSeenRegisteredOnboarding] = useState(true)

    useEffect(() => {
        setUsername(originalUsername ?? '')
        setEmail(originalEmail ?? '')
    }, [originalUsername, originalEmail])

    // Reset edit state when modal opens/closes
    useEffect(() => {
        if (typeof opened === 'boolean') {
            setEditing('')
            setResetEmailSent(false)
            setEmailError('')
            setRegistrationSuccess(false)
            if (opened) {
                setUsername(originalUsername ?? '')
                setEmail(originalEmail ?? '')
                // Fetch fresh balance when modal opens
                fetchBalance()

                // Check onboarding state
                if (user?.id) {
                    const seenGuest = !!localStorage.getItem(`onboarding-profile-guest-seen-${user.id}`)
                    const seenRegistered = !!localStorage.getItem(`onboarding-profile-registered-seen-${user.id}`)
                    setHasSeenGuestOnboarding(seenGuest)
                    setHasSeenRegisteredOnboarding(seenRegistered)

                    // Start tour when modal opens
                    if (isGuest && !seenGuest) {
                        setTimeout(() => setRunTour(true), 500)
                    } else if (!isGuest && !seenRegistered) {
                        setTimeout(() => setRunTour(true), 500)
                    }
                }
            } else {
                // Stop tour when modal closes
                setRunTour(false)
            }
        }
    }, [opened, originalUsername, originalEmail, user?.roles, user?.id, isGuest, fetchBalance])

    // Stop tour on unmount (navigating away)
    useEffect(() => {
        return () => {
            if (runTour) {
                setRunTour(false)
            }
        }
    }, [runTour])

    let usernameError
    if ((queryUsernameData && !queryUsernameData.usernameAvailable) ||
        (updateUserError && updateUserError.graphQLErrors?.find( error => error.extensions?.code === 'USERNAME_EXISTS' ))) {
        usernameError = 'That user name is not available.'
    }

    async function saveUsername() {
        if (!username || username === originalUsername) return
        try {
            await updateUser({ variables: { updates: { username } } })
            setEditing('')
        } catch (error) {
            setUsername(originalUsername)
            setEditing('')
        }
    }

    function handleEmailChange(value) {
        setEmail(value)
        setEmailError('')
    }

    function saveEmail() {
        if (!email || email === originalEmail) {
            setEditing('')
            return
        }

        if (!isValidEmail(email)) {
            setEmailError('The address is invalid')
            return
        }

        pushModal('confirmEmail', {
            email,
            onConfirm: confirmRegistration
        })
    }

    async function confirmRegistration(password) {
        try {
            await registerGuestMutation({
                variables: {
                    email,
                    password
                }
            })

            setRegistrationSuccess(true)
            setEditing('')
        } catch (error) {
            setEmailError('Registration failed. Please try again.')
            // Re-throw the error so ConfirmEmailModal can handle it
            throw error
        }
    }

    function updateAvatar() {
        const quickEditorCore = new GravatarQuickEditorCore( {
            email: user.email,
            scope: [ 'avatars' ],
            onOpened: () => console.log('Avatar editor opened')
        })
        quickEditorCore.open()
    }

    function handleUsernameChange(value) {
        setUsername(value)
        if (value !== originalUsername && value.length > 0) {
            queryUsername({ variables: { username: value } })
        }
    }

    function formatCreditBalance(nanodollars) {
        if (nanodollars == null) return '—'
        const dollars = nanodollars / 1e9
        return dollars.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 4 })
    }

    const guestTourSteps = [
        {
            target: '[data-tour="guest-username"]',
            content: "This is your guest username. You can only change your username if you register your account.",
            disableBeacon: true,
        },
        {
            target: '[data-tour="guest-email"]',
            content: "Enter your email address here to register your account.",
            disableBeacon: true,
        },
    ]

    const registeredTourSteps = [
        {
            target: '[data-tour="registered-username"]',
            content: "Since you are a registered user you can click here to change your username.",
            disableBeacon: true,
        },
    ]

    const handleJoyrideCallback = (data) => {
        const { status } = data

        if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
            // Mark onboarding as seen
            if (user?.id) {
                if (isGuest) {
                    localStorage.setItem(`onboarding-profile-guest-seen-${user.id}`, 'true')
                    setHasSeenGuestOnboarding(true)
                } else {
                    localStorage.setItem(`onboarding-profile-registered-seen-${user.id}`, 'true')
                    setHasSeenRegisteredOnboarding(true)
                }
            }
            setRunTour(false)
        }
    }

    const tourSteps = isGuest ? guestTourSteps : registeredTourSteps
    const shouldRunTour = runTour && ((isGuest && !hasSeenGuestOnboarding) || (!isGuest && !hasSeenRegisteredOnboarding))

    async function handleBuyCredit() {
        try {
            const successUrl = window.location.href
            const cancelUrl = window.location.href
            const { data } = await createCheckoutSession({
                variables: { successUrl, cancelUrl }
            })
            window.location.href = data.createCheckoutSession
        } catch (err) {
            pushModal('alert', {
                title: 'Error',
                text: 'Something went wrong starting the checkout. Please try again.',
                confirmText: 'OK'
            })
        }
    }

    async function resendVerificationEmail() {
        try {
            await sendVerificationMutation({ variables: { email: user.email } })
            setVerificationSent(true)
            pushModal('alert', {
                title: 'Verification Email Sent',
                text: 'Please check your inbox and click the verification link.',
                confirmText: 'OK'
            })
        } catch (err) {
            pushModal('alert', {
                title: 'Error',
                text: 'Something went wrong sending the verification email. Please try again.',
                confirmText: 'OK'
            })
        }
    }

    async function requestPasswordReset() {        try {
            const response = await resetPasswordMutation({
                variables: { email: user.email }
            })

            if (response.data?.resetPassword) {
                setResetEmailSent(true)
            }
        } catch (err) {
            console.error('Request password reset error:', err)
        }
    }

    return (
        <Modal
            opened={!!opened}
            onClose={onClose}
            title="Profile"
            size="xl"
            fullScreen={isMobile}
            {...rest}
            styles={{
                title: {
                    fontSize: '1.125rem',
                    fontWeight: 700,
                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
                },
                body: {
                    padding: isMobile ? '0.5rem' : 'md'
                }
            }}
            centered
            withinPortal
            withCloseButton
            lockScroll
        >
            <Joyride
                steps={tourSteps}
                run={shouldRunTour}
                continuous
                hideCloseButton
                showSkipButton
                disableOverlayClose
                disableCloseOnEsc
                locale={{ skip: 'Exit', last: 'Finish' }}
                callback={handleJoyrideCallback}
                styles={{
                    options: {
                        primaryColor: '#ec03fc',
                        zIndex: 10001,
                    },
                }}
            />
            <Box>
                {needsVerification && (
                    <Alert
                        color="red"
                        variant="filled"
                        mb="md"
                        styles={{ message: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem' } }}
                    >
                        <Text size="sm" style={{ flex: 1 }}>
                            Your email address has not been verified. Please check for email from Tunistry and click the verification link.
                        </Text>
                        <Button
                            size="xs"
                            variant="white"
                            color="red"
                            loading={sendVerificationLoading}
                            disabled={verificationSent}
                            onClick={resendVerificationEmail}
                        >
                            Resend Email
                        </Button>
                    </Alert>
                )}
                <Stack gap="md" style={{ marginTop: '1rem' }}>
                    {registrationSuccess && (
                        <Alert
                            color="green"
                            variant="light"
                            icon={<IconCheck size={16} />}
                        >
                            Account registered successfully! Please check your email to verify your account.
                        </Alert>
                    )}

                    {/* Basic Info Section */}
                    <Paper p="md" radius="md" withBorder style={{ background: 'rgba(255,255,255,0.05)' }}>
                        <Stack gap="sm">
                            {!isGuest ? (
                                <Group gap="xs" align="flex-start" wrap="nowrap">
                                    <Text size="sm" fw={500} c="dimmed" style={{ minWidth: '100px', flexShrink: 0 }}>
                                        Email
                                    </Text>
                                    <Text size="sm">{user?.email}</Text>
                                </Group>
                            ) : (
                                <>
                                    <div data-tour="guest-email">
                                        <EditableField
                                            label="Email"
                                            value={email}
                                            onChange={handleEmailChange}
                                            fieldName="email"
                                            editing={editing}
                                            setEditing={setEditing}
                                            onSave={saveEmail}
                                            canEdit={true}
                                        />
                                    </div>
                                    {emailError && (
                                        <Text size="sm" c="red" pl="100px">
                                            {emailError}
                                        </Text>
                                    )}
                                </>
                            )}

                            {!isGuest ? (
                                <>
                                    <div data-tour="registered-username">
                                        <EditableField
                                            label="Username"
                                            value={username}
                                            onChange={handleUsernameChange}
                                            fieldName="username"
                                            editing={editing}
                                            setEditing={setEditing}
                                            onSave={saveUsername}
                                            canEdit={true}
                                        />
                                    </div>
                                    {usernameError && (
                                        <Text size="sm" c="red" pl="100px">
                                            {usernameError}
                                        </Text>
                                    )}
                                </>
                            ) : (
                                <Group gap="xs" align="flex-start" wrap="nowrap" data-tour="guest-username">
                                    <Text size="sm" fw={500} c="dimmed" style={{ minWidth: '100px', flexShrink: 0 }}>
                                        Username
                                    </Text>
                                    <Text size="sm">{user?.username}</Text>
                                </Group>
                            )}

                            <Group gap="xs" align="flex-start" wrap="nowrap">
                                <Text size="sm" fw={500} c="dimmed" style={{ minWidth: '100px', flexShrink: 0 }}>
                                    Credit Balance
                                </Text>
                                <Group gap="xs">
                                    <Text size="sm">
                                        {formatCreditBalance(balanceData?.user?.llmCreditBalance ?? user?.llmCreditBalance)}
                                    </Text>
                                    <Button
                                        size="xs"
                                        variant="subtle"
                                        color="blue"
                                        onClick={handleBuyCredit}
                                        loading={checkoutLoading}
                                        leftSection={<IconCreditCard size={12} />}
                                    >
                                        Buy Credit
                                    </Button>
                                </Group>
                            </Group>
                        </Stack>
                    </Paper>

                    {/* Avatar Section */}
                    <Paper p="md" radius="md" withBorder style={{ background: 'rgba(255,255,255,0.05)' }}>
                        <Stack gap="sm">
                            <Title order={5} c="gray.3">Avatar</Title>
                            <Group gap="md" align="center">
                                <ProfileAvatar user={user} size={64} />
                                {!isGuest && (
                                    <Button onClick={updateAvatar} variant="light" size="sm">
                                        Update with Gravatar
                                    </Button>
                                )}
                            </Group>
                        </Stack>
                    </Paper>

                    {/* Password Section - Only for verified users */}
                    {!isGuest && (
                        <Paper p="md" radius="md" withBorder style={{ background: 'rgba(255,255,255,0.05)' }}>
                            <Stack gap="sm">
                                <Title order={5} c="gray.3">Password</Title>
                                {resetEmailSent ? (
                                    <Alert
                                        color="green"
                                        variant="light"
                                        icon={<IconCheck size={16} />}
                                    >
                                        Password reset link sent! Check your email at {user?.email} for instructions on how to reset your password.
                                    </Alert>
                                ) : (
                                    <Group gap="md" align="center">
                                        <Text size="sm" c="dimmed">
                                            Request a password reset link to be sent to your email
                                        </Text>
                                        <Button
                                            onClick={requestPasswordReset}
                                            variant="light"
                                            size="sm"
                                            loading={resetPasswordLoading}
                                            disabled={resetPasswordLoading}
                                        >
                                            Reset Password
                                        </Button>
                                    </Group>
                                )}
                            </Stack>
                        </Paper>
                    )}

                    {/* Logout Button */}
                    <Group justify="space-between" align="center" mt="md">
                        <Group gap="md" ml="sm">
                            <Anchor size="sm" href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</Anchor>
                            <Anchor size="sm" href="/terms" target="_blank" rel="noopener noreferrer">Terms of Service</Anchor>
                        </Group>
                        <Button onClick={logout} variant="filled" color="red">
                            Logout
                        </Button>
                    </Group>
                </Stack>
            </Box>
        </Modal>
    )
}
